import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import clsx from "clsx"
import { useStore } from "../../store/useStore"
import type { DiffState, RelationDiff, ScopeDiffState } from "../../types"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compute cell+relation-entry+row-freshness progress for a single scope diff */
function scopeProgress(diff: ScopeDiffState | DiffState): {
  total: number
  approved: number
} {
  let total = 0
  let approved = 0
  for (const rec of Object.values(diff.records)) {
    const cells = Object.values(rec.cells)
    let recTotal = cells.length
    let recApproved = cells.filter((c) => c.accepted !== null).length
    // Row-level freshness counts as one item
    if (rec.rowFreshnessAccepted !== undefined) {
      recTotal += 1
      if (rec.rowFreshnessAccepted !== null) recApproved += 1
    }
    const relDiffs = diff.relationDiffs?.[rec.recordId]
    if (relDiffs) {
      for (const rd of Object.values(relDiffs) as RelationDiff[]) {
        recTotal += rd.entries?.length ?? 0
        recApproved +=
          rd.entries?.filter((e) => e.accepted !== null).length ?? 0
      }
    }
    // Record with no reviewable items counts as 1/1 — nothing to review means complete
    if (recTotal === 0) {
      recTotal = 1
      recApproved = 1
    }
    total += recTotal
    approved += recApproved
  }
  return { total, approved }
}

/** Compute split stats: rows, primary cells, stale rows (secondary) */
function scopeSplitStats(diff: ScopeDiffState | DiffState) {
  let totalRows = 0
  let approvedRows = 0
  let totalPrimary = 0
  let approvedPrimary = 0
  let totalSecondary = 0 // stale rows needing freshness confirmation
  let approvedSecondary = 0

  for (const rec of Object.values(diff.records)) {
    const cells = Object.values(rec.cells)
    const hasContent =
      cells.length > 0 || rec.rowFreshnessAccepted !== undefined
    if (!hasContent) continue
    totalRows++

    let rowAllResolved = true
    for (const c of cells) {
      totalPrimary++
      if (c.accepted !== null) approvedPrimary++
      else rowAllResolved = false
    }
    // Row-level freshness as a "secondary" item
    if (rec.rowFreshnessAccepted !== undefined) {
      totalSecondary++
      if (rec.rowFreshnessAccepted !== null) approvedSecondary++
      else rowAllResolved = false
    }
    // Also count relation diff entries toward row resolution
    const relDiffs = diff.relationDiffs?.[rec.recordId]
    if (relDiffs) {
      for (const rd of Object.values(relDiffs) as RelationDiff[]) {
        const entries = rd.entries ?? []
        for (const e of entries) {
          if (e.accepted === null) rowAllResolved = false
        }
      }
    }
    if (rowAllResolved) approvedRows++
  }

  return {
    rows: { total: totalRows, approved: approvedRows },
    primary: { total: totalPrimary, approved: approvedPrimary },
    secondary: { total: totalSecondary, approved: approvedSecondary },
  }
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  diffState: DiffState | ScopeDiffState
  onCancel: () => void
  /** All scope diffs (for export: shows per-scope progress & gates Exportieren) */
  allScopeDiffs?: Record<string, ScopeDiffState>
  /** Scope names for display */
  scopeNames?: Record<string, string>
  /** Called when user clicks Exportieren (export diffs only) */
  onExport?: () => void
  /** True while export is in progress */
  exporting?: boolean
  /** Called when user clicks Importieren (import diffs only) */
  onImport?: () => void
  /** True while import commit is in progress */
  importing?: boolean
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DiffToolbar({
  diffState,
  onCancel,
  allScopeDiffs,
  scopeNames,
  onExport,
  exporting,
  onImport,
  importing,
}: Props) {
  const { translate } = useTranslation()
  const exportMode = !!diffState.exportMode
  const targetFormat = diffState.targetFormat
  const fileName = diffState.fileName

  const diffFilterMode = useStore((s) => s.diffFilterMode)
  const setDiffFilterMode = useStore((s) => s.setDiffFilterMode)

  // Split stats for current scope
  const stats = scopeSplitStats(diffState)

  // Main progress bar tracks ROWS, not cells — the user confirms rows, so rows
  // are the unit of review that matches the mental model. Cells/Aktualisierungen
  // remain available as filter pills.
  const rowTotal = stats.rows.total
  const rowApproved = stats.rows.approved
  const rowPct = rowTotal > 0 ? Math.round((rowApproved / rowTotal) * 100) : 0

  // Count type-incompatible cells (still useful to show)
  let typeIncompatibleCount = 0
  for (const rec of Object.values(diffState.records)) {
    for (const cell of Object.values(rec.cells)) {
      if (cell.typeIncompatible && cell.accepted === null)
        typeIncompatibleCount++
    }
  }

  // All-scopes progress (gates Importieren / Exportieren button)
  // Both primary and secondary cells must be fully resolved
  const hasMultiScope = allScopeDiffs && Object.keys(allScopeDiffs).length > 0
  let allScopesReady = true
  const scopeProgressList: {
    scopeId: string
    name: string
    total: number
    approved: number
  }[] = []

  if (hasMultiScope) {
    for (const [sid, sd] of Object.entries(allScopeDiffs)) {
      const prog = scopeProgress(sd)
      scopeProgressList.push({
        scopeId: sid,
        name: scopeNames?.[sid] ?? sid,
        ...prog,
      })
      if (prog.approved < prog.total) allScopesReady = false
    }
  }

  // Filter pills data
  const pills: {
    key: "rows" | "changes" | "updates"
    label: string
    approved: number
    total: number
  }[] = [
    {
      key: "rows",
      label: translate("QMS/DIFF/ROWS"),
      approved: stats.rows.approved,
      total: stats.rows.total,
    },
    {
      key: "changes",
      label: translate("QMS/DIFF/VALUES"),
      approved: stats.primary.approved,
      total: stats.primary.total,
    },
    {
      key: "updates",
      label: translate("QMS/DIFF/UPDATES"),
      approved: stats.secondary.approved,
      total: stats.secondary.total,
    },
  ]

  return (
    <div className="flex flex-col gap-2">
      {/* Main toolbar row */}
      <div className="flex items-center gap-3 rounded-lg border border-yellow5 bg-yellow5/20 px-4 py-2">
        {/* Left: info + filter pills */}
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex items-center gap-2">
            <span className="text-xs font-semibold text-yellow1">
              {targetFormat
                ? translate("QMS/DIFF/EXPORT_PREVIEW")
                : exportMode
                  ? translate("QMS/DIFF/EXPORT_DB_TO")
                  : translate("QMS/DIFF/REVIEW_CHANGES_OFF")}
            </span>
            {targetFormat ? (
              <span className="font-mono max-w-[200px] truncate rounded bg-yellow5/40 px-1.5 py-0.5 text-xs text-yellow3">
                {targetFormat === "excel"
                  ? "Excel"
                  : targetFormat === "produkt_konformitaet"
                    ? translate("QMS/DIFF/PRODUCT_CONFORMITY")
                    : targetFormat === "kunden_konformitaet"
                      ? translate("QMS/DIFF/CUSTOMER_CONFORMITY")
                      : targetFormat === "escha_kunden_konformitaet"
                        ? translate("QMS/DIFF/ESCHA_CUSTOMER_CONFORMITY")
                        : "CSV"}
              </span>
            ) : (
              <span className="font-mono max-w-[200px] truncate rounded bg-yellow5/40 px-1.5 py-0.5 text-xs text-yellow3">
                {fileName}
              </span>
            )}
          </div>

          {/* Filter pills */}
          <div className="flex items-center gap-1.5">
            {pills.map((pill) => {
              if (pill.total === 0) return null
              const isActive = diffFilterMode === pill.key
              const open = pill.total - pill.approved
              return (
                <button
                  key={pill.key}
                  onClick={() => setDiffFilterMode(pill.key)}
                  className={clsx(
                    "flex items-center gap-1.5 rounded px-2 py-1 text-[11px] font-medium transition-colors",
                    isActive
                      ? "bg-yellow1/20 text-yellow1 ring-1 ring-yellow1/40"
                      : "text-yellow3 hover:bg-yellow5/40 hover:text-yellow1",
                  )}
                >
                  <span>{pill.label}</span>
                  <span
                    className={clsx(
                      "font-mono tabular-nums",
                      open > 0 ? "text-yellow1" : "text-success",
                    )}
                  >
                    {pill.approved}/{pill.total}
                  </span>
                  {open > 0 && (
                    <span className="text-[10px] text-yellow3/70">
                      {translate("QMS/DIFF/OPEN_COUNT", { count: open })}
                    </span>
                  )}
                </button>
              )
            })}
            {typeIncompatibleCount > 0 && (
              <>
                <span className="text-[11px] text-yellow5">|</span>
                <span className="text-[11px] font-medium text-red1">
                  {translate("QMS/DIFF/TYPE_ERROR_COUNT", { count: typeIncompatibleCount })}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Center: progress bar — rows accepted vs required */}
        <div className="w-32 shrink-0">
          <div className="h-1.5 w-full rounded-full bg-yellow5">
            <div
              className="h-1.5 rounded-full bg-yellow1 transition-all duration-300"
              style={{ width: `${rowPct}%` }}
            />
          </div>
          <p className="mt-0.5 text-center text-[10px] text-yellow3">
            {translate("QMS/DIFF/ROWS_PROGRESS", { approved: rowApproved, total: rowTotal, pct: rowPct })}
          </p>
        </div>

        {/* Right: actions */}
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={onCancel}
            className="rounded-lg border border-grey4 px-3 py-1.5 text-xs font-medium text-grey5 transition-colors hover:bg-grey3 hover:text-white"
          >
            {translate("QMS/COMMON/CANCEL")}
          </button>
          {exportMode && onExport && (
            <button
              onClick={onExport}
              disabled={!allScopesReady || exporting}
              className="rounded-lg bg-yellow5 px-3 py-1.5 text-xs font-medium text-yellow1 transition-colors hover:bg-yellow5/80 disabled:opacity-40"
            >
              {exporting ? translate("QMS/DIFF/EXPORTING_BTN") : translate("QMS/DIFF/EXPORT_BTN")}
            </button>
          )}
          {!exportMode && onImport && (
            <button
              onClick={onImport}
              disabled={!allScopesReady || importing}
              className="rounded-lg bg-success px-3 py-1.5 text-xs font-medium text-black transition-colors hover:bg-success/80 disabled:opacity-40"
            >
              {importing ? translate("QMS/DIFF/IMPORTING_BTN") : translate("QMS/DIFF/IMPORT_BTN")}
            </button>
          )}
        </div>
      </div>

      {/* Per-scope progress is shown on the scope tabs instead */}
    </div>
  )
}
